import React, { useEffect, useState } from 'react';
import { validateDateRange } from '../../utils/dateHelpers';

const DateRangeSelector = ({
    range,
    setRange,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    requireApply = false, // New prop to enable "Apply" button for custom ranges
    onClear = null, // Optional callback to clear additional filters (e.g., search)
    defaultRange = '30', // Default range when clearing
    hideClear = false // Hide internal Clear button when parent provides its own (e.g., FilterActions)
}) => {
    const [dateRangeError, setDateRangeError] = useState('');
    const [pendingStart, setPendingStart] = useState('');
    const [pendingEnd, setPendingEnd] = useState('');

    // Initialize pending dates when switching to custom
    useEffect(() => {
        if (range === 'custom') {
            setPendingStart(customStart || '');
            setPendingEnd(customEnd || '');
        }
    }, [range, customStart, customEnd]);

    // Validate date range (max 365 days)
    useEffect(() => {
        // For requireApply mode, validate pending dates; otherwise validate actual dates
        const startToValidate = requireApply && range === 'custom' ? pendingStart : customStart;
        const endToValidate = requireApply && range === 'custom' ? pendingEnd : customEnd;
        
        const validation = validateDateRange(range, startToValidate, endToValidate);
        
        if (!validation.isValid) {
            setDateRangeError(validation.error);
        } else {
            setDateRangeError('');
        }
    }, [range, customStart, customEnd, pendingStart, pendingEnd, requireApply]);

    const handleApply = () => {
        if (pendingStart && pendingEnd) {
            setCustomStart(pendingStart);
            setCustomEnd(pendingEnd);
        }
    };

    const handleClear = () => {
        // Reset date range to default
        setRange(defaultRange);
        setCustomStart('');
        setCustomEnd('');
        setPendingStart('');
        setPendingEnd('');
        
        // Call optional callback to clear other filters
        if (onClear && typeof onClear === 'function') {
            onClear();
        }
    };

    const showApplyButton = requireApply && range === 'custom' && pendingStart && pendingEnd;
    const showClearButton = range !== defaultRange || customStart || customEnd || pendingStart || pendingEnd;

    return (
        <div className="flex flex-col space-y-2">
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
                <select
                    className="border border-gray-300 bg-white text-black rounded px-3 py-2"
                    value={range}
                    onChange={e => {
                        setRange(e.target.value);
                        // Clear pending dates when switching away from custom
                        if (e.target.value !== 'custom') {
                            setPendingStart('');
                            setPendingEnd('');
                        }
                    }}
                >
                    <option value="7">Last 7 days</option>
                    <option value="14">Last 14 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>              
                    <option value="180">Last 180 days</option>
                    <option value="365">Last 365 days</option>
                    <option value="ytd">Year to Date</option>
                    <option value="custom">Custom range</option>
                </select>
                
                {range === 'custom' && (
                    <>
                        <input
                            type="date"
                            className="border border-gray-300 bg-white text-black rounded px-3 py-2"
                            value={requireApply ? pendingStart : (customStart || '')}
                            onChange={e => requireApply ? setPendingStart(e.target.value) : setCustomStart(e.target.value)}
                            placeholder="Start date"
                        />
                        <input
                            type="date"
                            className="border border-gray-300 bg-white text-black rounded px-3 py-2"
                            value={requireApply ? pendingEnd : (customEnd || '')}
                            onChange={e => requireApply ? setPendingEnd(e.target.value) : setCustomEnd(e.target.value)}
                            placeholder="End date"
                        />
                        {showApplyButton && (
                            <button
                                onClick={handleApply}
                                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 font-medium transition-colors whitespace-nowrap"
                                disabled={!!dateRangeError}
                            >
                                Apply
                            </button>
                        )}
                    </>
                )}
                
                {/* Clear button - shows when filters are active (hidden when parent provides its own) */}
                {showClearButton && !hideClear && (
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium transition-colors whitespace-nowrap"
                        title="Clear all filters"
                    >
                        Clear
                    </button>
                )}
            </div>
            
            {/* Error message */}
            {dateRangeError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-800 flex items-start gap-2">
                    <span className="text-red-600 font-bold">⚠️</span>
                    <span>{dateRangeError}</span>
                </div>
            )}
        </div>
    );
};

export default DateRangeSelector;